import React, { useContext, useRef, useState } from 'react';
import { FaLightbulb, FaRocket, FaBrain, FaUsers, FaChartLine, FaShieldAlt, FaPlayCircle, FaArrowRight, FaCheck, FaTimes, FaChevronDown, FaPalette } from 'react-icons/fa';
import { ThemeContext } from '../context/ThemeContext';
import ThemeMenu from '../components/ThemeMenu';

function LandingPage({ openLoginModal, openSignupModal }) {
  const { theme } = useContext(ThemeContext);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const themeButtonRef = useRef(null);
  const featuresRef = useRef(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: <FaLightbulb className="text-4xl text-[var(--primary)]" />,
      title: "Idea Capture",
      description: "Dump any random thought or idea instantly. No structure required - just get it out of your head and into the system.",
      demo: "'What if we made shoes that could change color based on mood?' → Instantly saved and categorized"
    },
    {
      icon: <FaBrain className="text-4xl text-[var(--primary)]" />,
      title: "AI-Powered Enhancement",
      description: "The AI helps you develop, refine, and expand your ideas. Turn a simple thought into a structured concept.",
      demo: "AI suggests: market research, potential challenges, implementation steps, and related ideas"
    },
    {
      icon: <FaChartLine className="text-4xl text-[var(--primary)]" />,
      title: "Manageable Dashboard",
      description: "Transform chaos into clarity. Your ideas become organized, trackable, and actionable through an intuitive dashboard.",
      demo: "Visual progress tracking, idea connections, and priority management"
    },
    {
      icon: <FaRocket className="text-4xl text-[var(--primary)]" />,
      title: "Development Journey",
      description: "Follow your ideas from initial spark to potential implementation. Track progress and iterations.",
      demo: "Idea stages: Brainstorm → Research → Plan → Prototype → Launch"
    }
  ];

  const whatItIs = [
    "A creative idea dumping ground",
    "An AI-powered idea development tool", 
    "A personal innovation workspace",
    "A thought organization system",
    "A creativity enhancement platform"
  ];

  const whatItIsNot = [
    "A large project management system",
    "A team collaboration tool for big enterprises",
    "A complex workflow management platform",
    "A replacement for professional project tools",
    "A social media or sharing platform"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--bg-primary)] to-[var(--bg-secondary)]">
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(80px, -70px) rotate(90deg); }
          50% { transform: translate(-60px, 80px) rotate(180deg); }
          75% { transform: translate(-90px, -40px) rotate(270deg); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-100px, 90px) rotate(120deg); }
          66% { transform: translate(70px, -80px) rotate(240deg); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          20% { transform: translate(60px, 100px) rotate(72deg); }
          40% { transform: translate(-85px, -60px) rotate(144deg); }
          60% { transform: translate(95px, 30px) rotate(216deg); }
          80% { transform: translate(-45px, 75px) rotate(288deg); }
        }
      `}</style>
      {/* Minimal Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--background)] border-b border-[#fff3]/30 backdrop-blur-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-[var(--foreground)]">
            <FaLightbulb className="text-[var(--primary)]" size={20} />
            Idea Hub
          </div>
          <div className="flex items-center gap-3">
            <button
              ref={themeButtonRef}
              onClick={() => setShowThemeMenu(!showThemeMenu)}
              className="p-2 rounded-lg bg-[var(--muted)] hover:bg-[var(--muted-foreground)] text-[var(--foreground)] transition-colors"
              title="Change theme"
            >
              <FaPalette size={16} />
            </button>
            <button
              onClick={openLoginModal}
              className="px-4 py-2 bg-[var(--primary)] text-[var(--primary-foreground)] rounded-lg hover:bg-[var(--primary)]/90 transition-colors font-medium text-sm"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Theme Menu */}
      <ThemeMenu 
        isVisible={showThemeMenu}
        onClose={() => setShowThemeMenu(false)}
        buttonRef={themeButtonRef}
      />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--primary)] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" 
               style={{
                 animation: 'pulse 6s ease-in-out infinite, float1 35s ease-in-out infinite'
               }}></div>
          <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-[var(--secondary)] rounded-full mix-blend-multiply filter blur-xl opacity-20" 
               style={{
                 animation: 'pulse 6s ease-in-out infinite 2s, float2 40s ease-in-out infinite'
               }}></div>
          <div className="absolute bottom-1/4 left-1/2 w-72 h-72 bg-[var(--primary)] rounded-full mix-blend-multiply filter blur-xl opacity-20" 
               style={{
                 animation: 'pulse 6s ease-in-out infinite 4s, float3 38s ease-in-out infinite'
               }}></div>
        </div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <FaLightbulb className="text-8xl text-[var(--primary)] mx-auto mb-6 animate-bounce" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-[var(--text-primary)] mb-6 leading-tight">
            Idea <span className="text-[var(--primary)]">Hub</span>
          </h1>
          <p className="text-xl md:text-2xl text-[var(--text-secondary)] mb-8 leading-relaxed">
            Where random thoughts become structured possibilities
          </p>
          <p className="text-lg text-[var(--text-secondary)] mb-12 max-w-2xl mx-auto">
            Dump any idea, thought, or inspiration. The AI helps you develop it into something meaningful. 
            Transform creative chaos into organized innovation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openSignupModal}
              className="px-8 py-4 bg-[var(--primary)] text-white rounded-xl text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              Get Started <FaArrowRight />
            </button>
            <button
              onClick={openLoginModal}
              className="px-8 py-4 bg-transparent border-2 border-[var(--primary)] text-[var(--primary)] rounded-xl text-lg font-semibold hover:bg-[var(--primary)] hover:text-white transition-all duration-300 transform hover:scale-105"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6">
              How It Works
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              From scattered thoughts to structured ideas in four simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[var(--bg-secondary)] rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-[var(--border-color)]"
              >
                <div className="flex items-center mb-6">
                  <div className="mr-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[var(--text-primary)]">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-[var(--text-secondary)] mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <div className="bg-[var(--bg-primary)] rounded-lg p-4 border-l-4 border-[var(--primary)]">
                  <p className="text-sm text-[var(--text-secondary)] italic">
                    {feature.demo}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What It Is vs What It's Not */}
      <section className="py-20 px-6 bg-[var(--bg-secondary)]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6">
              Know What You're Getting
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              Clear expectations lead to better experiences
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* What It Is */}
            <div className={`rounded-2xl p-8 border-2 ${
              theme === 'dark' 
                ? 'bg-emerald-900/20 border-emerald-700' 
                : 'bg-emerald-50 border-emerald-200'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 flex items-center ${
                theme === 'dark' ? 'text-emerald-400' : 'text-emerald-900'
              }`}>
                <FaCheck className="mr-3" />
                What Idea Hub IS
              </h3>
              <ul className="space-y-4">
                {whatItIs.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <FaCheck className={`mt-1 mr-3 flex-shrink-0 ${
                      theme === 'dark' ? 'text-emerald-400' : 'text-emerald-800'
                    }`} />
                    <span className={`${
                      theme === 'dark' ? 'text-emerald-300' : 'text-emerald-950'
                    }`}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What It's Not */}
            <div className={`rounded-2xl p-8 border-2 ${
              theme === 'dark' 
                ? 'bg-rose-900/20 border-rose-700' 
                : 'bg-rose-50 border-rose-200'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 flex items-center ${
                theme === 'dark' ? 'text-rose-400' : 'text-rose-900'
              }`}>
                <FaTimes className="mr-3" />
                What Idea Hub is NOT
              </h3>
              <ul className="space-y-4">
                {whatItIsNot.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <FaTimes className={`mt-1 mr-3 flex-shrink-0 ${
                      theme === 'dark' ? 'text-rose-400' : 'text-rose-800'
                    }`} />
                    <span className={`${
                      theme === 'dark' ? 'text-rose-300' : 'text-rose-950'
                    }`}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Video/Demo Section (Placeholder) */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6">
            See It In Action
          </h2>
          <p className="text-xl text-[var(--text-secondary)] mb-12">
            Watch how Idea Hub transforms scattered thoughts into organized innovation
          </p>
          
          {/* Placeholder for video */}
          <div className="bg-[var(--bg-secondary)] rounded-2xl p-16 border-2 border-dashed border-[var(--border-color)] hover:border-[var(--primary)] transition-colors duration-300">
            <FaPlayCircle className="text-8xl text-[var(--text-secondary)] mx-auto mb-6 hover:text-[var(--primary)] transition-colors duration-300 cursor-pointer" />
            <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-4">
              Demo Video Coming Soon
            </h3>
            <p className="text-[var(--text-secondary)]">
              A comprehensive walkthrough of all features and capabilities
            </p>
          </div>
        </div>
      </section>

      {/* Access Information */}
      <section className="py-20 px-6 bg-[var(--bg-secondary)]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[var(--bg-primary)] rounded-2xl p-12 border border-[var(--border-color)] shadow-lg">
            <FaShieldAlt className="text-6xl text-[var(--primary)] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-6">
              Limited Access
            </h2>
            <p className="text-lg text-[var(--text-secondary)] mb-8 leading-relaxed">
              Idea Hub is currently in limited access mode. This is a hobby project with careful user management 
              to ensure a good experience for everyone.
            </p>
            <div className="bg-[var(--bg-secondary)] rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">
                How to Get Access
              </h3>
              <div className="text-[var(--text-secondary)]">
                <p>Contact me to request an invite</p>
              </div>
            </div>
            <button
              onClick={openSignupModal}
              className="px-8 py-4 bg-[var(--primary)] text-white rounded-xl text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Request Access
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[var(--border-color)]">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <FaLightbulb className="text-3xl text-[var(--primary)] mr-3" />
            <span className="text-2xl font-bold text-[var(--text-primary)]">Idea Hub</span>
          </div>
          <p className="text-[var(--text-secondary)]">
            Transform your creative chaos into organized innovation
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
